import React, { useMemo } from "react";

import { orderBy, uniqBy } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type Dimensions } from "@features/sales-v3/charts/chart";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useLatestTabAncestor } from "@features/sales-v3/hooks/use-latest-ancestor";
import {
  type AdKeyInfo,
  type Atomicity,
  type FiltersState,
  type TabState,
  defaultTabState,
  useTabState,
} from "@features/sales-v3/hooks/use-tab-state";
import { useDefaultTableSelectionIsSelectAll } from "@features/sales-v3/hooks/use-table-selection";
import {
  isKeyParent,
  isParent,
} from "@features/sales-v3/table/components/table";
import { useExpandedRowIds } from "@features/sales-v3/table/contexts";
import { type TopLevelBreakdown } from "@features/sales-v3/types";

import { TAB_RANKING } from "../constants/tab";

import { Tab } from "./tab";

export type TabId = "breakdown" | "campaign" | "adset" | "ad";

interface Props {
  activeTab: TabId;
  onActivate(tab: TabId): void;
}

export const Tabs = ({ activeTab, onActivate }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { breakdowns } = usePageConfiguration();
  const [, setExpandedRowIds] = useExpandedRowIds();
  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();
  const findAncestor = useLatestTabAncestor();

  const adTabState = useTabState("ad");
  const adsetTabState = useTabState("adset");
  const campaignTabState = useTabState("campaign");
  const breakdownTabState = useTabState("breakdown");
  const tabStates = useMemo(
    () => ({
      breakdown: breakdownTabState,
      campaign: campaignTabState,
      adset: adsetTabState,
      ad: adTabState,
    }),
    [breakdownTabState, campaignTabState, adsetTabState, adTabState],
  );

  const updateTabState = (toTabId: TabId) => {
    onActivate(toTabId);

    if (toTabId === activeTab) {
      return;
    }

    prettyLogEventPage({
      domain: "Tabs",
      action: "onClick",
      additionalActionInfo: "Change tab",
      data: {
        pageContext: { tabId: toTabId },
      },
    });

    // if you are moving to a higher granularity, ie from campaign to ad
    if (TAB_RANKING.indexOf(toTabId) > TAB_RANKING.indexOf(activeTab)) {
      // inherit the activeTab's properties
      tabStates[toTabId].setTabState((toTabState) => {
        const currentTabState = tabStates[activeTab].tabState;

        // reconcile the states
        const reconciledStates = reconcileAppliedAndProposedTabFilters(
          currentTabState.appliedFiltersState ?? null,
          currentTabState.proposedFiltersState,
          isDefaultSelectAll,
        );

        const { toAddExclusions, appliedAdKeyFilters } =
          enrichInclusionsAndExclusions({
            breakdowns,
            reconciledStates,
            currentTabState,
          });

        const newTabState = {
          ...toTabState,
          preselectionDone: false,
          selectAll: currentTabState.selectAll,
          appliedFiltersState: {
            // apply the reconciled state between the current
            inclusionAtomicities: reconciledStates.inclusionAtoms,
            exclusionAtomicities: [
              ...reconciledStates.exclusionAtoms,
              ...toAddExclusions,
            ],
            campaignExclusionAdKeys:
              currentTabState?.appliedFiltersState?.campaignExclusionAdKeys,
            campaignInclusionAdKeys:
              appliedAdKeyFilters?.campaignInclusionFilters,
            adsetInclusionAdKeys: appliedAdKeyFilters?.adsetInclusionFilters,
            adsetExclusionAdKeys:
              currentTabState?.appliedFiltersState?.adsetExclusionAdKeys,
            [`${activeTab}ExclusionAdKeys`]:
              currentTabState.proposedFiltersState.exclusionAdKeys,
            [`${activeTab}InclusionAdKeys`]:
              currentTabState.proposedFiltersState.inclusionAdKeys,
          },
        } as TabState;

        return newTabState;
      });
    } else {
      const tabsToUnset = TAB_RANKING.slice(
        TAB_RANKING.indexOf(toTabId) + 1,
        TAB_RANKING.length,
      );

      tabsToUnset.forEach((tab) => tabStates[tab].unsetTabState());

      const toTabState = tabStates[toTabId];
      if (!toTabState.tabState.appliedFiltersState) {
        const { ancestor: latestAncestor, ancestorId } = findAncestor(toTabId);

        // reconcile the states
        const reconciledStates = reconcileAppliedAndProposedTabFilters(
          latestAncestor?.appliedFiltersState ?? null,
          latestAncestor?.proposedFiltersState ??
            defaultTabState.proposedFiltersState,
          isDefaultSelectAll,
        );

        const { toAddExclusions, appliedAdKeyFilters } =
          enrichInclusionsAndExclusions({
            breakdowns,
            reconciledStates,
            currentTabState: latestAncestor ?? defaultTabState,
          });

        const newTabState = {
          ...toTabState.tabState,
          preselectionDone: false,
          selectAll: latestAncestor?.selectAll,
          appliedFiltersState: {
            // apply the reconciled state between the current
            inclusionAtomicities: reconciledStates.inclusionAtoms,
            exclusionAtomicities: [
              ...reconciledStates.exclusionAtoms,
              ...toAddExclusions,
            ],
            campaignExclusionAdKeys:
              latestAncestor?.appliedFiltersState?.campaignExclusionAdKeys,
            campaignInclusionAdKeys:
              appliedAdKeyFilters?.campaignInclusionFilters,
            adsetInclusionAdKeys: appliedAdKeyFilters?.adsetInclusionFilters,
            adsetExclusionAdKeys:
              latestAncestor?.appliedFiltersState?.adsetExclusionAdKeys,
            [`${ancestorId}ExclusionAdKeys`]:
              latestAncestor?.proposedFiltersState.exclusionAdKeys,
            [`${ancestorId}InclusionAdKeys`]:
              latestAncestor?.proposedFiltersState.inclusionAdKeys,
          },
        } as TabState;
        tabStates[toTabId].setTabState(newTabState);
      }
    }

    setExpandedRowIds([]);
  };

  return (
    <>
      <div className="flex gap-1 text-sm relative">
        <div className="absolute bottom-0 w-full border-b border-gray-0 z-[1]" />
        <Tab
          id="breakdown"
          key="breakdown"
          active={activeTab === "breakdown"}
          onClick={() => updateTabState("breakdown")}
        >
          {!breakdowns.length ||
          (breakdowns.length === 1 &&
            breakdowns[0].key === "Platform (Northbeam)")
            ? "Platform"
            : "Breakdown"}
        </Tab>
        <Tab
          id="campaign"
          key="campaign"
          active={activeTab === "campaign"}
          onClick={() => updateTabState("campaign")}
        >
          Campaign
        </Tab>
        <Tab
          id="adset"
          key="adset"
          active={activeTab === "adset"}
          onClick={() => updateTabState("adset")}
        >
          Adset
        </Tab>
        <Tab
          id="ad"
          key="ad"
          active={activeTab === "ad"}
          onClick={() => updateTabState("ad")}
        >
          Ad
        </Tab>
      </div>
    </>
  );
};

export function reconcileAppliedAndProposedTabFilters(
  appliedState: FiltersState | null,
  proposedState: FiltersState,
  isDefaultSelectAll: boolean,
) {
  // there as been no proposedState then just use the applied state
  if (
    !proposedState?.inclusionAtomicities?.length &&
    !proposedState?.exclusionAtomicities?.length &&
    !proposedState?.inclusionAdKeys?.length &&
    !proposedState?.exclusionAdKeys?.length
  ) {
    return {
      exclusionAtoms: appliedState?.exclusionAtomicities ?? [],
      inclusionAtoms: appliedState?.inclusionAtomicities ?? [],
    };
  }
  if (!appliedState) {
    return {
      exclusionAtoms: proposedState.exclusionAtomicities,
      inclusionAtoms: proposedState.inclusionAtomicities,
    };
  }

  const appliedInclusionAtoms: Atomicity[] = [];
  if (!isDefaultSelectAll) {
    const proposedInclusionsSet = new Set(
      proposedState.inclusionAtomicities.map((i) => i.atomicity),
    );
    appliedState.inclusionAtomicities.forEach((i) => {
      const atom = JSON.parse(i.atomicity) as Dimensions[];
      if (atom.length === 1) {
        if (!proposedInclusionsSet.has(i.atomicity)) {
          return;
        }
      }
      appliedInclusionAtoms.push(i);
      return;
    });
  }
  const reconciledInclusions = uniqBy(
    appliedInclusionAtoms.concat(proposedState.inclusionAtomicities),
    (a) => a.atomicity,
  );
  const reconciledExclusions = uniqBy(
    appliedState.exclusionAtomicities.concat(
      proposedState.exclusionAtomicities,
    ),
    (a) => a.atomicity,
  );

  const reconciledInclusionsSet = new Set(
    reconciledInclusions.map((i) => i.atomicity),
  );
  const reconciledExclusionsSet = new Set(
    reconciledExclusions.map((i) => i.atomicity),
  );

  // for each inclusion, check it has direct child inclusions
  reconciledInclusions.forEach((inclusion) => {
    // if inclusion exists in exclusions list, throw it out
    if (reconciledExclusionsSet.has(inclusion.atomicity)) {
      reconciledInclusionsSet.delete(inclusion.atomicity);
      return;
    }

    // check if there are any children
    const inclusionChildrenByShallowest = getChildrenShallowestFirst(
      inclusion,
      reconciledInclusions.filter((i) => i.atomicity !== inclusion.atomicity),
    );
    // if there are no inclusion add do nothing
    if (!inclusionChildrenByShallowest.length) {
      return;
    }

    const exclusionParentsByShallowest = getChildrenShallowestFirst(
      inclusion,
      reconciledExclusions,
    );

    /* if there are no exlcusions, remove the inclusion since it means there is a clear path to
       inclusion children */
    if (!exclusionParentsByShallowest.length) {
      reconciledInclusionsSet.delete(inclusion.atomicity);
      return;
    }

    if (
      toParsedAtom(inclusionChildrenByShallowest[0].atomicity).length <
      toParsedAtom(exclusionParentsByShallowest[0].atomicity).length
    ) {
      reconciledInclusionsSet.delete(inclusion.atomicity);
      return;
    }
  });

  reconciledExclusions.forEach((exclusion) => {
    const exclusionParentsByDeepest = getParentsDeepestFirst(
      exclusion,
      reconciledExclusions.filter((i) => i.atomicity !== exclusion.atomicity),
    );

    // if there are no exlcusions
    if (!exclusionParentsByDeepest.length) {
      // do nothing
      return;
    }

    // check if there are any parents
    const inclusionChildrenByDeepest = getParentsDeepestFirst(
      exclusion,
      reconciledInclusions.filter((i) => i.atomicity !== exclusion.atomicity),
    );

    /* if there are no exlcusions, remove the inclusion since it means there is a clear path to
       inclusion children */
    if (!inclusionChildrenByDeepest.length) {
      reconciledExclusionsSet.delete(exclusion.atomicity);
      return;
    }

    if (
      toParsedAtom(exclusionParentsByDeepest[0].atomicity).length >
      toParsedAtom(inclusionChildrenByDeepest[0].atomicity).length
    ) {
      reconciledExclusionsSet.delete(exclusion.atomicity);
      return;
    }
  });
  return {
    exclusionAtoms: Array.from(reconciledExclusionsSet).map((i) => ({
      atomicity: i,
      count: 0,
    })),
    inclusionAtoms: Array.from(reconciledInclusionsSet).map((i) => ({
      atomicity: i,
      count: 0,
    })),
  };
}

function toParsedAtom(atomicity: string) {
  return JSON.parse(atomicity) as Dimensions[];
}

function getChildrenShallowestFirst(
  atom: Atomicity,
  potentialChildren: Atomicity[],
) {
  const parsedAtom = JSON.parse(atom.atomicity);
  const childAtoms: Atomicity[] = [];
  potentialChildren.forEach((potentialChild) => {
    const parsed = JSON.parse(potentialChild.atomicity) as Dimensions[];
    if (
      parsed.length > parsedAtom.length &&
      isParent(atom.atomicity, potentialChild.atomicity)
    ) {
      childAtoms.push(potentialChild);
    }
  });

  return orderBy(
    childAtoms,
    (a) => {
      const parsed = JSON.parse(a.atomicity) as Dimensions[];
      return parsed.length;
    },
    "asc",
  );
}

function getParentsDeepestFirst(
  atom: Atomicity,
  potentialParents: Atomicity[],
) {
  const parsedAtom = JSON.parse(atom.atomicity);
  const parentAtoms: Atomicity[] = [];
  potentialParents.forEach((potentialParent) => {
    const parsed = JSON.parse(potentialParent.atomicity) as Dimensions[];
    if (
      parsed.length > parsedAtom.length &&
      isParent(potentialParent.atomicity, atom.atomicity)
    ) {
      parentAtoms.push(potentialParent);
    }
  });

  return orderBy(
    parentAtoms,
    (a) => {
      const parsed = JSON.parse(a.atomicity) as Dimensions[];
      return parsed.length;
    },
    "desc",
  );
}

function filterOutUsefulAppliedAdKeys(
  appliedFiltersState: FiltersState | undefined,
  proposedFilters: FiltersState,
) {
  const campaignInclusionFilters = filterOutAdKeyInclusionsWithParents(
    appliedFiltersState?.campaignInclusionAdKeys,
    proposedFilters.inclusionAtomicities.concat(
      proposedFilters.exclusionAtomicities,
    ),
  );
  const adsetInclusionFilters = filterOutAdKeyInclusionsWithParents(
    appliedFiltersState?.adsetInclusionAdKeys,
    proposedFilters.inclusionAtomicities.concat(
      proposedFilters.exclusionAtomicities,
    ),
  );
  return {
    campaignInclusionFilters,
    adsetInclusionFilters,
  };
}

function filterOutAdKeyInclusionsWithParents(
  adKeys: AdKeyInfo[] | undefined,
  potentialParentAtoms: Atomicity[],
) {
  if (!adKeys) {
    return;
  }

  // we only want inclusions that have parents
  return adKeys.filter((adKeyInfo) => {
    if (!potentialParentAtoms?.length) {
      return true;
    }
    // adKey has a parent
    return potentialParentAtoms.some((atom) =>
      isKeyParent(adKeyInfo.dimensions, atom.atomicity),
    );
  });
}

export function enrichInclusionsAndExclusions({
  breakdowns,
  currentTabState,
  reconciledStates,
}: {
  breakdowns: TopLevelBreakdown[];
  currentTabState: TabState;
  reconciledStates: {
    inclusionAtoms: Atomicity[];
    exclusionAtoms: Atomicity[];
  };
}) {
  if (
    !currentTabState.proposedFiltersState.inclusionAtomicities?.length &&
    currentTabState?.proposedFiltersState?.inclusionAdKeys.length
  ) {
    return {
      toAddExclusions: [],
      appliedAdKeyFilters: {
        adsetInclusionFilters: [],
        campaignInclusionFilters: [],
      },
    };
  }
  const toAddExclusions: Atomicity[] = [];
  if (breakdowns.length) {
    // add in exclusions at the highest level if there is no direct ancestor exclusion
    currentTabState?.proposedFiltersState?.inclusionAdKeys?.forEach(
      (inclusionAdKey) => {
        const exclusionsAtomParents = orderBy(
          reconciledStates.exclusionAtoms.filter((atom) => {
            return isKeyParent(inclusionAdKey.dimensions, atom.atomicity);
          }),
          (a) => {
            const parsed = JSON.parse(a.atomicity) as Dimensions[];
            return parsed.length;
          },
          "desc",
        ).map((i) => JSON.parse(i.atomicity) as Dimensions[]);

        const inclusionAtomParents = orderBy(
          reconciledStates.inclusionAtoms.filter((atom) => {
            return isKeyParent(inclusionAdKey.dimensions, atom.atomicity);
          }),
          (a) => {
            const parsed = JSON.parse(a.atomicity) as Dimensions[];
            return parsed.length;
          },
          "desc",
        ).map((i) => JSON.parse(i.atomicity) as Dimensions[]);

        if (!exclusionsAtomParents.length && !inclusionAtomParents.length) {
          toAddExclusions.push({
            atomicity: JSON.stringify([
              {
                [breakdowns[0].key]:
                  inclusionAdKey.dimensions[breakdowns[0].key],
              },
            ]),
            count: 0,
          });
        }

        if (
          // the inclusion atom is the most direct parent, then add in a direct child exclusion
          (inclusionAtomParents?.[0]?.length ?? 0) >
          (exclusionsAtomParents?.[0]?.length ?? 0)
        ) {
          if (inclusionAtomParents[0].length === breakdowns.length) {
            return;
          }
          toAddExclusions.push({
            atomicity: JSON.stringify([
              ...inclusionAtomParents[0],
              {
                [breakdowns[inclusionAtomParents[0].length].key]:
                  inclusionAdKey.dimensions[
                    breakdowns[inclusionAtomParents[0].length].key
                  ],
              },
            ]),
            count: 0,
          });
          return;
        }
      },
    );
  }

  const appliedAdKeyFilters = filterOutUsefulAppliedAdKeys(
    currentTabState.appliedFiltersState,
    currentTabState.proposedFiltersState,
  );

  return { toAddExclusions, appliedAdKeyFilters };
}
